/* eslint-disable @typescript-eslint/no-explicit-any */
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import EditIcon from '@material-ui/icons/Edit';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';

import {
  ADMINS_TABLE_FIELDS,
  CARE_PROVIDERS_TABLE_FIELDS,
  DRAWER_TABS,
  PATIENTS_TABLE_FIELDS,
  TABLE_FIELDS,
  TABLE_FIELDS_LABLES,
} from '../../../constants';
import { User } from '../../../shared/types';

type AdminTableProps = {
  selectedDrawerTab: string | undefined;
  users: Array<Partial<User>> | undefined;
  handelEditDialog: (user: Partial<User>) => void;
};

type TableDataProps = {
  users: Array<Partial<User>>;
  selectedDrawerTab: string | undefined;
  handelEditDialog: (user: Partial<User>) => void;
};

const useStyles = makeStyles((theme) => ({
  cell: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  editIcon: {
    width: '19px',
    height: '19px',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
  },
}));

const TableData: React.FC<TableDataProps> = ({
  users,
  handelEditDialog,
  selectedDrawerTab,
}) => {
  const classes = useStyles();

  // filter users properties based on the selected drawer tab: Patients, Care Providers or Admins
  const getUsersData = () => {
    const usersCopy = [...users];
    const usersDataToRender: Array<Partial<User>> = [];
    let tableFields: Array<TABLE_FIELDS> = [];

    if (selectedDrawerTab === DRAWER_TABS.PATIENTS) {
      tableFields = PATIENTS_TABLE_FIELDS;
    }
    if (selectedDrawerTab === DRAWER_TABS.CARE_PROVIDERS) {
      tableFields = CARE_PROVIDERS_TABLE_FIELDS;
    }
    if (selectedDrawerTab === DRAWER_TABS.ADMINS) {
      tableFields = ADMINS_TABLE_FIELDS;
    }

    // eslint-disable-next-line array-callback-return
    usersCopy.map((user: Partial<User>) => {
      Object.keys(user).forEach((key) => {
        if (!tableFields.find((field) => field === key) && key !== 'id') {
          user = _.omit(user, key);
        }
      });
      usersDataToRender.push(user);
    });

    return usersDataToRender;
  };

  const formatValue = (key: string, value: any) => {
    if (key === TABLE_FIELDS.DATE_OF_BIRTH) {
      return moment(value).format('MMM Do, YYYY');
    }
    return value;
  };

  return (
    <TableBody>
      {getUsersData().map((user, index) => (
        <TableRow key={index}>
          {Object.values(user).map((value, index) => {
            const userKey = Object.keys(user)[index];
            return (
              value &&
              // id and clinic_role aren't fields to render on the table
              userKey !== TABLE_FIELDS.ID &&
              userKey !== TABLE_FIELDS.CLINIC_ROLE && (
                <TableCell key={index} className={classes.cell}>
                  {formatValue(userKey, value)}
                </TableCell>
              )
            );
          })}
          <TableCell className={classes.cell}>
            <EditIcon
              className={classes.editIcon}
              onClick={() => handelEditDialog(user)}
            />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
};

export const AdminTable: React.FC<AdminTableProps> = ({
  selectedDrawerTab,
  users,
  handelEditDialog,
}) => {
  const classes = useStyles();
  const [page, setPage] = useState<number>(0);
  const [usersOnPage, setUsersOnPage] = useState<Array<Partial<User>>>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const tableFieldLabels = () => {
    switch (selectedDrawerTab) {
      case DRAWER_TABS.PATIENTS: {
        return [
          TABLE_FIELDS_LABLES.NAME,
          TABLE_FIELDS_LABLES.EMAIL,
          TABLE_FIELDS_LABLES.DATE_OF_BIRTH,
          TABLE_FIELDS_LABLES.STATUS,
        ];
      }
      case DRAWER_TABS.CARE_PROVIDERS: {
        return [TABLE_FIELDS_LABLES.NAME, TABLE_FIELDS_LABLES.EMAIL];
      }
      case DRAWER_TABS.ADMINS: {
        return [TABLE_FIELDS_LABLES.NAME, TABLE_FIELDS_LABLES.EMAIL];
      }
      default: {
        return [
          TABLE_FIELDS_LABLES.NAME,
          TABLE_FIELDS_LABLES.EMAIL,
          TABLE_FIELDS_LABLES.DATE_OF_BIRTH,
          TABLE_FIELDS_LABLES.STATUS,
          TABLE_FIELDS_LABLES.CLINIC_ROLE,
        ];
      }
    }
  };

  useEffect(() => {
    const firstUserIndex = page * rowsPerPage + (!page ? 0 : 1);
    const lastUserIndex = firstUserIndex + rowsPerPage;
    if (users) {
      setUsersOnPage(
        users.filter(
          (_, index) => index >= firstUserIndex && index < lastUserIndex
        )
      );
    }
  }, [page, rowsPerPage, JSON.stringify(users)]);

  useEffect(() => {
    setPage(0);
  }, [selectedDrawerTab]);

  return (
    <>
      <TableContainer component={Paper}>
        <Table aria-label="admin table">
          <TableHead>
            <TableRow>
              {tableFieldLabels().map((fieldName) => (
                <TableCell key={fieldName} className={classes.cell}>
                  <strong>{fieldName}</strong>
                </TableCell>
              ))}
              <TableCell className={classes.cell}>
                <strong>Edit</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableData
            users={usersOnPage}
            selectedDrawerTab={selectedDrawerTab}
            handelEditDialog={handelEditDialog}
          />
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={(users && users.length - 1) || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </TableContainer>
    </>
  );
};
